import { useCallback, useState } from 'react';

import { addMonths, format, startOfMonth, subMonths } from 'date-fns';
import { HiChevronLeft, HiChevronRight } from 'react-icons/hi';
import { twMerge } from 'tailwind-merge';

import { Button } from '../../../Button';

import { Calendar } from './Calendar';

interface DateBoxCommonProps {
  readonly rangePicker?: boolean;
  readonly startDate: Date;
  readonly endDate?: Date;
  readonly dates?: [Date | null, Date | null];
  readonly onPickDate?: (date: Date) => void;
  readonly className?: string;
}

interface DateBoxSingleProps {
  readonly rangePicker?: false;
  readonly endDate?: never;
}

interface DateBoxRangeProps {
  readonly rangePicker: true;
  readonly endDate: Date;
}

type DateBoxProps = DateBoxCommonProps & (DateBoxRangeProps | DateBoxSingleProps);

function DateBox({ rangePicker, startDate, endDate, dates, onPickDate, className }: DateBoxProps) {
  const classes = twMerge('flex flex-col px-2 py-6', className);
  const date = endDate ?? startDate;
  const [calendar, setCalendar] = useState(startOfMonth(new Date(date)));

  const onGoToPrevMonth = useCallback(() => {
    setCalendar((cal) => subMonths(cal, 1));
  }, []);

  const onGoToNextMonth = useCallback(() => {
    setCalendar((cal) => addMonths(cal, 1));
  }, []);

  return (
    <div className={classes}>
      <div className="flex flex-row">
        <div className="flex flex-auto basis-3/5 flex-row items-center border-b border-neutral-200 pb-4">
          <Button variant="nude" size="s" aria-label="Previous Month" onClick={onGoToPrevMonth} className="h-8">
            <HiChevronLeft size={18} />
          </Button>
          <h5 className="flex grow justify-center py-1 text-center text-base font-semibold text-neutral-900">
            {format(calendar, 'MMMM yyyy')}
          </h5>
          <Button variant="nude" size="s" aria-label="Next Month" onClick={onGoToNextMonth} className="h-8">
            <HiChevronRight size={18} />
          </Button>
        </div>
      </div>
      <div className="mt-2 flex flex-auto flex-col">
        {rangePicker ? (
          <Calendar
            rangePicker
            startDate={startDate}
            endDate={endDate}
            dates={dates}
            month={calendar.getMonth()}
            year={calendar.getFullYear()}
            onPickDate={onPickDate}
          />
        ) : (
          <Calendar startDate={startDate} dates={dates} month={calendar.getMonth()} year={calendar.getFullYear()} onPickDate={onPickDate} />
        )}
      </div>
    </div>
  );
}

export { DateBox };
